import React from 'react'

/**
 * The brand marks, inlined.
 *
 * Source of truth is `docs/brand/` in the saxonberg repo — these are the
 * `-mono` variants, which is deliberate. The `-white` variants hard-code
 * their casing to #002868 (the design system's Old Glory Blue), and this
 * site's grounds are #041E42 / #02132C. Dropping a `-white` mark here
 * would halo every ring crossing in the wrong navy.
 *
 * So: ink follows `currentColor`, casing follows `--ground`, and every
 * caller MUST set --ground to whatever is actually behind the mark.
 *
 * Assignment, per docs/brand/README.md:
 *   rings -> Saxonberg (the game)      hex -> Pantera's Box (the studio)
 */

/**
 * Pantera's Box. A regular hexagon divides into exactly three rhombi and
 * no other equal division exists — three falls out of the shape rather
 * than being imposed on it.
 */
export const HexMark = ({ size = 74, title, ...rest }) => (
  <svg
    viewBox="0 0 100 100"
    width={size}
    height={size}
    role={title ? 'img' : 'presentation'}
    aria-hidden={title ? undefined : 'true'}
    focusable="false"
    {...rest}
  >
    {title ? <title>{title}</title> : null}
    <path
      d="M50 50 L17.09 31 L50 12 L82.91 31 Z"
      fill="currentColor"
      transform="translate(50 31) scale(0.895) translate(-50 -31)"
    />
    <path
      d="M50 50 L82.91 31 L82.91 69 L50 88 Z"
      fill="currentColor"
      transform="translate(66.46 59.5) scale(0.895) translate(-66.46 -59.5)"
    />
    <path
      d="M50 50 L50 88 L17.09 69 L17.09 31 Z"
      fill="currentColor"
      transform="translate(33.55 59.5) scale(0.895) translate(-33.55 -59.5)"
    />
  </svg>
)

/**
 * Saxonberg, APPLIED — the seal. Use where the mark sits on a surface it
 * does not own; the masthead marks above own theirs and stay standalone.
 *
 * ⚠ This carries an outer white rim the source SVG does not have. The
 * brand README says the rim IS the required red/blue separation, but in
 * `saxonberg-seal.svg` both white rules are inset, leaving ~2.45 units of
 * bare red at the outer edge — so the file as drawn puts red against blue,
 * the one thing the system forbids. The artwork is reproduced here exactly,
 * scaled to 0.949 inside a white disc, so the rim is the only addition.
 * If the source file grows a rim, drop the disc and the scale.
 */
export const SealMark = ({ size = 96, id = 'seal', title, ...rest }) => {
  const clip = `${id}-over`
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : 'true'}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <circle cx="50" cy="50" r="50" fill="#FFFFFF" />
      <g transform="translate(50 50) scale(0.949) translate(-50 -50)">
        <defs>
          <clipPath id={clip}>
            <circle cx="24.348" cy="60.047" r="9.87" />
            <circle cx="59.152" cy="39.953" r="9.87" />
          </clipPath>
        </defs>
        <circle cx="50" cy="50" r="49" fill="#BF0A30" />
        <circle cx="50" cy="50" r="45.5" fill="none" stroke="#FFFFFF" strokeWidth="2.1" />
        <circle cx="50" cy="50" r="41" fill="none" stroke="#FFFFFF" strokeWidth="0.9" opacity="0.8" />
        <g transform="translate(50 50) scale(0.72) translate(-50 -50)">
          <circle cx="50" cy="64.289" r="26" fill="none" stroke="#BF0A30" strokeWidth="11.985" />
          <circle cx="50" cy="64.289" r="26" fill="none" stroke="#FFFFFF" strokeWidth="4.7" />
          <circle cx="66.5" cy="35.711" r="26" fill="none" stroke="#BF0A30" strokeWidth="11.985" />
          <circle cx="66.5" cy="35.711" r="26" fill="none" stroke="#FFFFFF" strokeWidth="4.7" />
          <circle cx="33.5" cy="35.711" r="26" fill="none" stroke="#BF0A30" strokeWidth="11.985" />
          <circle cx="33.5" cy="35.711" r="26" fill="none" stroke="#FFFFFF" strokeWidth="4.7" />
          <circle
            cx="50"
            cy="64.289"
            r="26"
            fill="none"
            stroke="#BF0A30"
            strokeWidth="11.985"
            clipPath={`url(#${clip})`}
          />
          <circle
            cx="50"
            cy="64.289"
            r="26"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="4.7"
            clipPath={`url(#${clip})`}
          />
        </g>
      </g>
    </svg>
  )
}

/**
 * Saxonberg. Genuine Borromean: each ring passes over one neighbour and
 * under the other, so no two are linked yet all three hold. Cut any one
 * and the remaining two fall apart untouched — the Make / Fund / Play
 * argument. The casing stroke beneath each ink stroke is what interrupts
 * the ring passing underneath; without it three same-colour rings simply
 * stack and no crossing reads at all.
 *
 * `id` must be unique per instance on the page — the clip path is
 * referenced by url(#...), and duplicate ids would cross-wire the
 * over/under and silently break the topology.
 */
export const RingsMark = ({ size = 74, id = 'rings', title, ...rest }) => {
  const clip = `${id}-over`
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : 'true'}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <clipPath id={clip}>
          <circle cx="24.348" cy="60.047" r="9.87" />
          <circle cx="59.152" cy="39.953" r="9.87" />
        </clipPath>
      </defs>
      <circle cx="50" cy="64.289" r="26" fill="none" stroke="var(--ground)" strokeWidth="11.985" />
      <circle cx="50" cy="64.289" r="26" fill="none" stroke="currentColor" strokeWidth="4.7" />
      <circle cx="66.5" cy="35.711" r="26" fill="none" stroke="var(--ground)" strokeWidth="11.985" />
      <circle cx="66.5" cy="35.711" r="26" fill="none" stroke="currentColor" strokeWidth="4.7" />
      <circle cx="33.5" cy="35.711" r="26" fill="none" stroke="var(--ground)" strokeWidth="11.985" />
      <circle cx="33.5" cy="35.711" r="26" fill="none" stroke="currentColor" strokeWidth="4.7" />
      <circle
        cx="50"
        cy="64.289"
        r="26"
        fill="none"
        stroke="var(--ground)"
        strokeWidth="11.985"
        clipPath={`url(#${clip})`}
      />
      <circle
        cx="50"
        cy="64.289"
        r="26"
        fill="none"
        stroke="currentColor"
        strokeWidth="4.7"
        clipPath={`url(#${clip})`}
      />
    </svg>
  )
}
